import { createContext, useContext } from "react";
import type { ElementType, FocusEvent, KeyboardEvent, ReactNode } from "react";

// Inline click-to-edit context. Mirrors the SiteLinkContext pattern in link.tsx:
// the public website never provides this context, so `editing` is false and
// <Editable> renders plain, non-interactive markup — byte-for-byte the same page
// it rendered before. The admin live-preview provides { editing: true, onEdit }
// to turn the rendered page itself into a WYSIWYG edit surface: clicking any
// editable text makes it contentEditable, and committing calls onEdit(path, value)
// with the dot-path of the field that changed.
export interface EditContextValue {
  editing: boolean;
  onEdit: (path: string, value: string) => void;
}

const DEFAULT: EditContextValue = { editing: false, onEdit: () => {} };

export const EditContext = createContext<EditContextValue>(DEFAULT);
export const EditProvider = EditContext.Provider;

export function useEditContext(): EditContextValue {
  return useContext(EditContext);
}

// Immutably set a value at a dot-path (e.g. "hero.ctas.0.label") on a content
// object, cloning only the nodes along the path so React state updates stay pure.
export function setAtPath<T>(root: T, path: string, value: unknown): T {
  const keys = path.split(".");
  const clone: unknown = Array.isArray(root) ? [...(root as unknown[])] : { ...(root as object) };
  let cur = clone as Record<string, unknown>;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const child = cur[k];
    cur[k] = Array.isArray(child) ? [...child] : { ...(child as object) };
    cur = cur[k] as Record<string, unknown>;
  }
  cur[keys[keys.length - 1]] = value;
  return clone as T;
}

// A single editable text field. In view mode it's just <Tag>{value}</Tag>. In
// edit mode it becomes contentEditable with a dashed hover outline; Enter (or
// blur) commits the new text, Escape cancels. Multi-line fields pass singleLine={false}.
export function Editable({
  path,
  as,
  value,
  className,
  singleLine = true,
}: {
  path: string;
  as?: ElementType;
  value: string;
  className?: string;
  singleLine?: boolean;
  children?: ReactNode;
}) {
  const { editing, onEdit } = useEditContext();
  const Tag = (as ?? "span") as ElementType;

  if (!editing) {
    return <Tag className={className}>{value}</Tag>;
  }

  return (
    <Tag
      className={className}
      data-cms-path={path}
      data-cms-editable="true"
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      onBlur={(e: FocusEvent<HTMLElement>) => {
        const next = (e.currentTarget.textContent ?? "").trim();
        if (next !== value) onEdit(path, next);
      }}
      onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
        if (e.key === "Enter" && (singleLine || !e.shiftKey)) {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === "Escape") {
          e.currentTarget.textContent = value; // revert
          e.currentTarget.blur();
        }
      }}
    >
      {value}
    </Tag>
  );
}
